/** @format */
import { ChangeDetectionStrategy, Component, DestroyRef, inject, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { Module, OtmId, TimingTemplate } from 'common-typescript/types';
import { Observable } from 'rxjs';
import { mono, RequestQueue, requestQueueIgnoreConcurrent } from 'sis-common';
import { AlertsService, AppErrorHandler } from 'sis-components';
import { FirstLevelModuleService } from '../../service/first-level-module.service';
import { TimingActionService } from '../../service/timing-action.service';
import { TimingTemplateSelection } from '../../timing.types';

export function timingTemplateModalOpener(): (viewContainerRef: ViewContainerRef) => MatDialogRef<TimingTemplateModalComponent, null> {
    const fudisDialogService = inject(FudisDialogService);
    return viewContainerRef =>
        fudisDialogService.open<TimingTemplateModalComponent, null, null>(TimingTemplateModalComponent, { viewContainerRef });
}

@Component({
    selector: 'app-timing-template-modal',
    standalone: false,
    templateUrl: './timing-template-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimingTemplateModalComponent {
    private readonly alertsService = inject(AlertsService);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly fudisDialogService = inject(FudisDialogService);
    private readonly timingActionService = inject(TimingActionService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly selectedTimingTemplates: Map<OtmId, TimingTemplateSelection> = new Map<OtmId, TimingTemplateSelection>();

    private readonly introductionQueue: RequestQueue<readonly TimingTemplateSelection[], void> = requestQueueIgnoreConcurrent(
        selections => mono(this.timingActionService.introduceTimingTemplatesToPlan(selections)),
        () => this.onSuccess(),
        this.appErrorHandler.defaultErrorHandler(),
        takeUntilDestroyed(this.destroyRef),
    );

    protected readonly firstLevelModules$: Observable<readonly Module[]> = inject(FirstLevelModuleService).firstLevelModules$.pipe(
        this.appErrorHandler.defaultErrorHandler(),
    );

    private onSuccess(): void {
        this.alertsService.success('TIMELINE.TIMING_TEMPLATE.ADD_SUCCESS');
        this.fudisDialogService.close();
    }

    protected updateSelectedTimingTemplates(module: Module, template: TimingTemplate | null) {
        if (!template) {
            this.selectedTimingTemplates.delete(module.id);
            return;
        }
        const selection: TimingTemplateSelection = { module, timingTemplate: template };
        this.selectedTimingTemplates.set(module.id, selection);
    }

    protected introduceTimingTemplates() {
        const selections: readonly TimingTemplateSelection[] = Array.from(this.selectedTimingTemplates.values());
        if (selections.length === 0) {
            // no templates selected, just close the modal
            this.fudisDialogService.close();
            return;
        }
        this.introductionQueue.next(selections);
    }
}
